import { z } from 'zod';
import { NODE_CATEGORIES } from '@/domain/funnel/node-system';

/**
 * SPEC-011 §11/plan §3-6 — the `PresentationGraphExtension` registered on
 * the canonical `GraphDocument` (docs/architecture/graph-schema.md §3) as
 * `graph.presentation`. This module stays framework-free (no React/xyflow),
 * mirroring how SPEC-009's `simulation` extension is defined.
 */

export const PRESENTATION_THEMES = ['dark', 'light'] as const;
export type PresentationTheme = (typeof PRESENTATION_THEMES)[number];
export const DEFAULT_PRESENTATION_THEME: PresentationTheme = 'dark'; // PRES-REQ-004

export const PRESENTATION_BRANDING_MODES = ['dark-funnels', 'client', 'minimal'] as const;
export type PresentationBrandingMode = (typeof PRESENTATION_BRANDING_MODES)[number];

export const PRESENTATION_STAGE_CATEGORIES = NODE_CATEGORIES;
export type PresentationStageCategory = (typeof PRESENTATION_STAGE_CATEGORIES)[number];

export const HIGHLIGHT_EMPHASIS_LEVELS = ['normal', 'strong'] as const;
export type HighlightEmphasis = (typeof HIGHLIGHT_EMPHASIS_LEVELS)[number];

// §68/plan §35 — validation limits, reused by both validation.ts and the UI.
export const TITLE_MAX = 160;
export const SUBTITLE_MAX = 300;
export const COMPANY_NAME_MAX = 160;
export const NOTE_TITLE_MAX = 120;
export const NOTE_BODY_MAX = 4000;
export const HIGHLIGHT_LABEL_MAX = 120;

export const StrategicNoteSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1).max(NOTE_TITLE_MAX),
  body: z.string().max(NOTE_BODY_MAX).default(''),
  nodeIds: z.array(z.string()).default([]),
  order: z.number().int(),
  visible: z.boolean().default(true),
});
export type StrategicNote = z.infer<typeof StrategicNoteSchema>;

export const HighlightSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1).max(HIGHLIGHT_LABEL_MAX),
  nodeIds: z.array(z.string()).default([]),
  edgeIds: z.array(z.string()).default([]),
  order: z.number().int(),
  emphasis: z.enum(HIGHLIGHT_EMPHASIS_LEVELS).default('normal'),
  noteId: z.string().optional(),
});
export type Highlight = z.infer<typeof HighlightSchema>;

export const PresentationViewportSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  zoom: z.number().finite().positive(),
});
export type PresentationViewport = z.infer<typeof PresentationViewportSchema>;

/** §11/plan §7 — the only thing registered on `GraphDocument.presentation`. */
export const PresentationGraphExtensionSchema = z.object({
  title: z.string().max(TITLE_MAX).optional(),
  subtitle: z.string().max(SUBTITLE_MAX).optional(),
  companyName: z.string().max(COMPANY_NAME_MAX).optional(),
  companyLogoAssetId: z.string().optional(),
  theme: z.enum(PRESENTATION_THEMES).default(DEFAULT_PRESENTATION_THEME),
  brandingMode: z.enum(PRESENTATION_BRANDING_MODES).default('dark-funnels'),
  coverEnabled: z.boolean().default(false),
  strategicNotes: z.array(StrategicNoteSchema).default([]),
  highlights: z.array(HighlightSchema).default([]),
  defaultViewport: PresentationViewportSchema.optional(),
  showMetrics: z.boolean().default(false),
});
export type PresentationGraphExtension = z.infer<typeof PresentationGraphExtensionSchema>;

export function emptyPresentationExtension(): PresentationGraphExtension {
  return {
    theme: DEFAULT_PRESENTATION_THEME,
    brandingMode: 'dark-funnels',
    coverEnabled: false,
    strategicNotes: [],
    highlights: [],
    showMetrics: false,
  };
}

export function createPresentationId(): string {
  return crypto.randomUUID();
}

// --- Logo asset (§21-24) -----------------------------------------------------

/** §23 — SVG is explicitly deferred until a sanitizer exists. */
export const ALLOWED_LOGO_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const MAX_LOGO_BYTES = 5 * 1024 * 1024;

export function validateLogoInput(input: { mimeType: string; sizeBytes: number }): string | null {
  if (!(ALLOWED_LOGO_MIME_TYPES as readonly string[]).includes(input.mimeType)) {
    return 'Unsupported logo type. Use PNG, JPEG or WEBP.';
  }
  if (input.sizeBytes > MAX_LOGO_BYTES) return 'Logo exceeds the 5 MB limit.';
  if (input.sizeBytes <= 0) return 'Logo file is empty.';
  return null;
}

export interface PresentationAssetRef {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

// --- Warnings (PRES-REQ-016/§69/§70) ---------------------------------------

export const PRESENTATION_WARNING_CODES = [
  'note_missing_node_refs',
  'highlight_missing_refs',
  'highlight_empty',
] as const;
export type PresentationWarningCode = (typeof PRESENTATION_WARNING_CODES)[number];

export interface PresentationWarning {
  code: PresentationWarningCode;
  message: string;
  entityId: string;
}
